<!-- The one-tap question board.

     Endurance and the Daily both draw from every mode, so both need a single
     board that can render a true/false, a two-way comparison, a four-way odd
     one out and a guess-the-thing option set without caring which it got. That
     is what quickfire.js `toQuickfire` normalises them into.

     After an answer, the right one is always shown — including when he got it
     wrong. A speed game that never tells you the answer teaches nothing, and
     this is the only moment it is free to. That is also why the CELEBRATION on
     a correct answer is conditional in app.css rather than tied to `.right`:
     the correct option is painted green whether or not he found it, and a
     button that throws a ring off itself for an answer he missed is the
     interface congratulating him for losing.

     THE PROMPT IS KEYED. It is replaced in place on a shared board, so at
     Endurance pace one sentence became another between frames with nothing
     marking the boundary. The key gives each question its own 200ms fade. -->
<template>
  <div class="q-stage">
    <span class="q-topic">
      <span aria-hidden="true">{{ topicEmoji(question.topic) }}</span>
      {{ topicLabel(question.topic) }}
    </span>

    <div
      v-if="question.glyph"
      class="guess-glyph"
      :style="{ '--blur': revealed ? '0px' : '10px' }"
      aria-hidden="true"
    >
      {{ question.glyph }}
    </div>

    <p :key="question.prompt" class="q-prompt">{{ question.prompt }}</p>

    <div class="answers" :class="{ 'two-up': question.options.length === 2 }">
      <button
        v-for="(opt, i) in question.options"
        :key="i"
        type="button"
        class="answer"
        :class="optionClass(i)"
        :disabled="revealed || locked"
        @click="$emit('answer', i)"
      >
        {{ opt }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { topicEmoji, topicLabel } from '../game/topics.js';

const props = defineProps({
  question: { type: Object, required: true },
  /** Set once he has answered, to paint the result and lock the buttons. */
  revealed: { type: Boolean, default: false },
  /**
   * Buttons off WITHOUT painting the answer.
   *
   * These are two different states and folding them together gave the answer
   * away. Endurance disables the board during its 3-2-1 countdown, and doing
   * that by passing `revealed` meant the correct option was painted green
   * behind the countdown overlay - hidden only because the overlay happened to
   * be opaque and full-screen. One change to that overlay and the headline
   * mode would have been showing every answer before the clock started.
   */
  locked: { type: Boolean, default: false },
  /** Which option he picked. -1 means the clock ran out on him. */
  chosen: { type: Number, default: -1 },
});

defineEmits(['answer']);

function optionClass(i) {
  if (!props.revealed) return '';
  if (i === props.question.answer) return 'right';
  if (i === props.chosen) return 'wrong';
  return 'muted';
}
</script>
